import {
  Box,
  Button,
  Flex,
  Input,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Divider,
  Code,
} from '@chakra-ui/react';
import { useState, useContext } from 'react';
import { FaEye, FaSearch } from 'react-icons/fa';
import MainCard from '../../../components/MainCard/Index';
import { configApi } from '../../../services/configApi';
import { useQuery } from '@tanstack/react-query';
import { ContextAuth } from '../../../contexts/Authentication';
import AuditLogsMobilePage from './Mobile';

interface IAuditLog {
  id: string;
  user_id: string;
  role: string;
  action: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  category: string;
  resource_type: string;
  resource_id?: string;
  selective_process_id?: string;
  step_id?: string;
  message: string;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

interface IAuditSearchParams {
  start_date?: string;
  end_date?: string;
  level?: string;
  category?: string;
  action?: string;
  resource_type?: string;
  page?: number;
  page_size?: number;
}

interface IAuditSearchResponse {
  results: IAuditLog[];
  count: number;
  next: string | null;
  previous: string | null;
}

const AuditLogsPage: React.FC = () => {
  const { isMobile } = useContext(ContextAuth);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLog, setSelectedLog] = useState<IAuditLog | null>(null);
  const [filters, setFilters] = useState<IAuditSearchParams>({
    page: 1,
    page_size: 25,
  });

  // Renderizar versão mobile
  if (isMobile) {
    return <AuditLogsMobilePage />;
  }

  const bgCard = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Fetch audit logs
  const { data, isLoading, refetch } = useQuery<IAuditSearchResponse>({
    queryKey: ['audit-logs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
      const response = await configApi.get(`/audit/my-logs/?${params.toString()}`);
      return response.data;
    },
    retry: 1,
  });

  const handleViewDetails = (log: IAuditLog) => {
    setSelectedLog(log);
    onOpen();
  };

  const getLevelColor = (level: string) => {
    const colors = {
      info: 'blue',
      warning: 'orange',
      error: 'red',
      critical: 'purple',
    };
    return colors[level as keyof typeof colors] || 'gray';
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const handleClearFilters = () => {
    setFilters({ page: 1, page_size: 25 });
  };

  return (
    <>
      <MainCard title="ÁREA DO CANDIDATO - " subtitle="AUDITORIA DE LOGS">
        <Box bg={bgCard} borderRadius="md" borderWidth="1px" borderColor={borderColor} p={6}>
          {/* Filtros */}
          <Box mb={6}>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              🔍 Filtros de Pesquisa
            </Text>
            
            <Flex gap={4} flexWrap="wrap" mb={4}>
              <Box flex="1" minW="200px">
                <Text fontSize="sm" mb={2}>Data Inicial</Text>
                <Input
                  type="date"
                  value={filters.start_date || ''}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                />
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="sm" mb={2}>Data Final</Text>
                <Input
                  type="date"
                  value={filters.end_date || ''}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                />
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="sm" mb={2}>Nível</Text>
                <Select
                  value={filters.level || ''}
                  onChange={(e) => setFilters({ ...filters, level: e.target.value })}
                >
                  <option value="">Todos os níveis</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                  <option value="critical">Critical</option>
                </Select>
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="sm" mb={2}>Categoria</Text>
                <Select
                  value={filters.category || ''}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                >
                  <option value="">Todas as categorias</option>
                  <option value="authentication">Autenticação</option>
                  <option value="profile_management">Perfil</option>
                  <option value="registration">Inscrição</option>
                  <option value="document_management">Documentos</option>
                  <option value="step_progress">Etapas</option>
                  <option value="data_export">Exportação</option>
                  <option value="system">Sistema</option>
                </Select>
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="sm" mb={2}>Ação</Text>
                <Input
                  placeholder="Ex: login, create, update..."
                  value={filters.action || ''}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                />
              </Box>

              <Box flex="1" minW="200px">
                <Text fontSize="sm" mb={2}>Tipo de Recurso</Text>
                <Select
                  value={filters.resource_type || ''}
                  onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
                >
                  <option value="">Todos os tipos</option>
                  <option value="user_profile">Perfil</option>
                  <option value="user_session">Sessão</option>
                  <option value="registration">Inscrição</option>
                  <option value="document_upload">Documento</option>
                  <option value="step_submission">Etapa</option>
                </Select>
              </Box>

              <Box flex="1" minW="150px">
                <Text fontSize="sm" mb={2}>Itens por Página</Text>
                <Select
                  value={filters.page_size || 25}
                  onChange={(e) => setFilters({ ...filters, page_size: Number(e.target.value), page: 1 })}
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Select>
              </Box>
            </Flex>

            <Flex gap={2}>
              <Button
                leftIcon={<FaSearch />}
                colorScheme="blue"
                onClick={() => refetch()}
                isLoading={isLoading}
              >
                Buscar
              </Button>
              <Button variant="outline" onClick={handleClearFilters}>
                Limpar Filtros
              </Button>
            </Flex>
          </Box>

          <Divider my={4} />

          {/* Tabela de Logs */}
          {isLoading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="xl" />
            </Flex>
          ) : (
            <>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>DATA E HORA</Th>
                      <Th>AÇÃO</Th>
                      <Th>NÍVEL</Th>
                      <Th>CATEGORIA</Th>
                      <Th>RECURSO</Th>
                      <Th>MENSAGEM</Th>
                      <Th textAlign="center">DETALHES</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data?.results?.map((log) => (
                      <Tr key={log.id}>
                        <Td fontSize="xs">{formatTimestamp(log.timestamp)}</Td>
                        <Td>
                          <Badge colorScheme="cyan" fontSize="xs">
                            {log.action}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getLevelColor(log.level)} fontSize="xs">
                            {log.level.toUpperCase()}
                          </Badge>
                        </Td>
                        <Td fontSize="xs">{log.category}</Td>
                        <Td fontSize="xs">{log.resource_type}</Td>
                        <Td fontSize="xs" maxW="300px" isTruncated>
                          {log.message}
                        </Td>
                        <Td textAlign="center">
                          <IconButton
                            aria-label="Ver detalhes"
                            icon={<FaEye />}
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleViewDetails(log)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Paginação */}
              <Flex justify="space-between" align="center" mt={4}>
                <Text fontSize="sm">
                  Mostrando {data?.results?.length || 0} de {data?.count || 0} registros
                </Text>
                <HStack>
                  <Button
                    size="sm"
                    onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
                    isDisabled={!data?.previous || isLoading}
                  >
                    Anterior
                  </Button>
                  <Text fontSize="sm">Página {filters.page || 1}</Text>
                  <Button
                    size="sm"
                    onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
                    isDisabled={!data?.next || isLoading}
                  >
                    Próxima
                  </Button>
                </HStack>
              </Flex>
            </>
          )}
        </Box>
      </MainCard>

      {/* Modal de Detalhes */}
      <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>📋 Detalhes do Log</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedLog && (
              <VStack align="stretch" spacing={4}>
                {/* Informações Básicas */}
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>
                    Informações Básicas
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">ID:</Text>
                      <Code fontSize="xs">{selectedLog.id}</Code>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Data/Hora:</Text>
                      <Text>{formatTimestamp(selectedLog.timestamp)}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Ação:</Text>
                      <Badge colorScheme="cyan">{selectedLog.action}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Nível:</Text>
                      <Badge colorScheme={getLevelColor(selectedLog.level)}>
                        {selectedLog.level.toUpperCase()}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Categoria:</Text>
                      <Text>{selectedLog.category}</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Divider />

                {/* Recursos */}
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>
                    Recursos
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Tipo de Recurso:</Text>
                      <Text>{selectedLog.resource_type}</Text>
                    </HStack>
                    {selectedLog.resource_id && (
                      <HStack justify="space-between">
                        <Text fontWeight="semibold">ID do Recurso:</Text>
                        <Code fontSize="xs">{selectedLog.resource_id}</Code>
                      </HStack>
                    )}
                    {selectedLog.selective_process_id && (
                      <HStack justify="space-between">
                        <Text fontWeight="semibold">Processo Seletivo:</Text>
                        <Code fontSize="xs">{selectedLog.selective_process_id}</Code>
                      </HStack>
                    )}
                    {selectedLog.step_id && (
                      <HStack justify="space-between">
                        <Text fontWeight="semibold">Etapa:</Text>
                        <Code fontSize="xs">{selectedLog.step_id}</Code>
                      </HStack>
                    )}
                  </VStack>
                </Box>

                <Divider />

                {/* Mensagem */}
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>
                    Mensagem
                  </Text>
                  <Box
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {selectedLog.message}
                    </Text>
                  </Box>
                </Box>

                {/* Informações Técnicas */}
                {(selectedLog.ip_address || selectedLog.user_agent) && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" fontSize="lg" mb={3}>
                        Informações Técnicas
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        {selectedLog.ip_address && (
                          <HStack justify="space-between">
                            <Text fontWeight="semibold">IP Address:</Text>
                            <Code fontSize="xs">{selectedLog.ip_address}</Code>
                          </HStack>
                        )}
                        {selectedLog.user_agent && (
                          <Box>
                            <Text fontWeight="semibold" mb={2}>User Agent:</Text>
                            <Code
                              fontSize="xs"
                              display="block"
                              p={2}
                              whiteSpace="normal"
                              wordBreak="break-all"
                            >
                              {selectedLog.user_agent}
                            </Code>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  </>
                )}

                {/* Contexto Adicional */}
                {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" fontSize="lg" mb={3}>
                        Contexto Adicional
                      </Text>
                      <Code
                        display="block"
                        p={3}
                        fontSize="xs"
                        whiteSpace="pre"
                        overflowX="auto"
                      >
                        {JSON.stringify(selectedLog.context, null, 2)}
                      </Code>
                    </Box>
                  </>
                )}

                {/* Usuário */}
                <Divider />
                <Box>
                  <Text fontWeight="bold" fontSize="lg" mb={3}>
                    Usuário
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">User ID:</Text>
                      <Code fontSize="xs">{selectedLog.user_id}</Code>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Role:</Text>
                      <Badge>{selectedLog.role}</Badge>
                    </HStack>
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AuditLogsPage;
